import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';

import { useIsLoggedInQuery } from '../redux/apis/authApi';
import { publicRoutes } from './PublicRoutes';
import { privateRoutes } from './PrivateRoute';
import { MainOutLet } from './mainLayOut';
import PermissionDenied from '../components/Auth/PermissionDenied';
import Page404 from '../components/Auth/Page404';
import { AuthOutLet } from './authLayOut';
import ContactUsPublic from '../screens/Pages/ContactUs/ContactUsPublic';
import { ROLES, normalizeUserLocality } from '../utils/constant/global';

const { ADMIN, HR } = ROLES;

const normalizeRoutePath = (path = '') => {
  const trimmed = String(path).trim().replace(/\/+$/, '');
  if (!trimmed) return '';
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

const pathMatches = (allowedPath, currentPath) => {
  const normalized = normalizeRoutePath(allowedPath);
  if (!normalized) return false;

  if (normalized === currentPath) return true;

  if (normalized.includes(':')) {
    const regex = new RegExp('^' + normalized.replace(/:\w+/g, '[^/]+') + '$');
    return regex.test(currentPath);
  }

  return currentPath.startsWith(`${normalized}/`);
};

// Extra guards beyond the role-module grant, keyed by path:
//  - Guest Check-In (/contact-submissions): AU users with an Admin/HR role
//  - Room Booking   (/room-booking)       : any AU user
const isAu = (user) => normalizeUserLocality(user?.locality ?? user?.nationality).includes('AU');

const isAuAdminOrHr = (user) => isAu(user) && [ADMIN, HR].includes(user?.hrxRole?.roleName);

const RESTRICTED_PATHS = {
  '/contact-submissions': isAuAdminOrHr,
  '/room-booking': isAu
};

const PrivateRouteWrapper = ({ element, allowedPaths, permissionsReady, user }) => {
  const location = useLocation();
  const currentPath = normalizeRoutePath(location.pathname);

  if (currentPath === '/permission-denied') return element;

  if (!permissionsReady) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const hasPermission =
    allowedPaths.some((path) => pathMatches(path, currentPath)) || currentPath === '/dashboard';

  const restrictedCheck = RESTRICTED_PATHS[currentPath];
  const passesRestriction = !restrictedCheck || restrictedCheck(user);

  if (!hasPermission || !passesRestriction) {
    return <Navigate to="/permission-denied" replace />;
  }

  return element;
};

const PublicRouteWrapper = ({ isAuthenticated, element }) => {
  const location = useLocation();

  const publicPaths = publicRoutes.map((r) => r.path);

  if (isAuthenticated && publicPaths.includes(location.pathname)) {
    return <Navigate to="/dashboard" replace />;
  }
  return element;
};

const RoutesComp = ({ isAuthenticated, allowedPaths, permissionsReady, user }) => (
  <Routes>
    {/* 🌐 STANDALONE PUBLIC PAGE (no auth, no layout) — opened via QR code */}
    <Route path="/contact-us" element={<ContactUsPublic />} />

    {/* 🔐 PRIVATE ROUTES WITH LAYOUT */}
    <Route element={<MainOutLet />}>
      {privateRoutes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <PrivateRouteWrapper
              element={route.element}
              allowedPaths={allowedPaths}
              permissionsReady={permissionsReady}
              user={user}
            />
          }
        />
      ))}
    </Route>
    {/* 🔁 Default */}
    <Route element={<AuthOutLet />}>
      {/* 🌍 PUBLIC ROUTES */}
      {publicRoutes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={<PublicRouteWrapper element={route.element} isAuthenticated={isAuthenticated} />}
        />
      ))}
    </Route>
    <Route path="/permission-denied" element={<PermissionDenied />} />
    <Route path="*" element={<Page404 />} />
  </Routes>
);

/* =========================
   Main Route
========================= */

export default function MainRoute() {
  const { data, isLoading, refetch } = useIsLoggedInQuery();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // ✅ Backend allowed paths (+ Time Doctor override)
  const allowedPaths = useMemo(() => {
    const paths = data?.data?.hrxRole?.modules?.map((m) => m.path).filter(Boolean) || [];
    const normalized = paths.map(normalizeRoutePath);

    const isAdmin = data?.data?.hrxRole?.roleName === ADMIN;
    const canAccessTimeDoctor =
      isAdmin || (Boolean(data?.data?.isTimedoctorUser) && Boolean(data?.data?.timeDoctorUserId));

    if (canAccessTimeDoctor && !normalized.includes('/attendance-employees')) {
      normalized.push('/attendance-employees');
    }

    return normalized;
  }, [data]);

  const permissionsReady = Boolean(data?.data?.hrxRole);

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      setIsAuthenticated(Boolean(data));
    }
  }, [isLoading, data]);

  return (
    <Suspense fallback={<Spinner animation="border" />}>
      <RoutesComp
        isAuthenticated={isAuthenticated}
        allowedPaths={allowedPaths}
        permissionsReady={permissionsReady}
        user={data?.data}
      />
    </Suspense>
  );
}
